import { trackPhoneCall } from "@/lib/googleAds";
import { useEffect } from 'react';
import { Link } from 'wouter';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import BreadcrumbSchema from '@/components/BreadcrumbSchema';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, ArrowRight } from 'lucide-react';
import { CanonicalTag } from '@/components/CanonicalTag';
import { OpenGraphTags } from '@/components/OpenGraphTags';

const serviceAreas = [
  { name: 'Santa Barbara', slug: 'santa-barbara', county: 'Santa Barbara County' },
  { name: 'Ventura', slug: 'ventura', county: 'Ventura County' },
  { name: 'Montecito', slug: 'montecito', county: 'Santa Barbara County' },
  { name: 'Goleta', slug: 'goleta', county: 'Santa Barbara County' },
  { name: 'Carpinteria', slug: 'carpinteria', county: 'Santa Barbara County' },
  { name: 'Ojai', slug: 'ojai', county: 'Ventura County' },
  { name: 'Camarillo', slug: 'camarillo', county: 'Ventura County' },
  { name: 'Solvang', slug: 'solvang', county: 'Santa Barbara County' },
  { name: 'Buellton', slug: 'buellton', county: 'Santa Barbara County' },
  { name: 'Santa Ynez', slug: 'santa-ynez', county: 'Santa Barbara County' },
  { name: 'Isla Vista', slug: 'isla-vista', county: 'Santa Barbara County' },
  { name: 'Summerland', slug: 'summerland', county: 'Santa Barbara County' },
  { name: 'Oxnard', slug: 'oxnard', county: 'Ventura County' },
  { name: 'Los Angeles', slug: 'los-angeles', county: 'Los Angeles County' }
];

const groupedByCounty = serviceAreas.reduce((acc, area) => {
  if (!acc[area.county]) {
    acc[area.county] = [];
  }
  acc[area.county].push(area);
  return acc;
}, {} as Record<string, typeof serviceAreas>);

export default function ServiceAreas() {
  const canonicalUrl = "https://danispainting.net/service-areas";
  
  useEffect(() => {
    document.title = 'Service Areas | Danis Painting Co.';
  }, []);

  const breadcrumbItems = [
    { label: 'Home', url: '/' },
    { label: 'Service Areas', url: '/service-areas' }
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CanonicalTag url={canonicalUrl} />
      <OpenGraphTags
        title="Service Areas | Danis Painting Co."
        description="Danis Painting Co. proudly serves Santa Barbara, Ventura, Montecito, Goleta, Carpinteria, Ojai, Camarillo, Solvang, Buellton, Santa Ynez, and Isla Vista with professional painting and waterproofing services."
        image="https://danispainting.net/logo.png"
        url="https://danispainting.net/service-areas"
        type="website"
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 md:pt-36 md:pb-20 text-white overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #0a1628 100%)' }}>
          <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: 'url(https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/hero-bg_5849f6b9_e05061fa.webp)' }}></div>
          <div className="container px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                Service Areas
              </h1>
              <p className="text-lg md:text-xl text-white/80">
                Danis Painting Co. proudly serves Southern and Central California with professional painting and waterproofing services.
              </p>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <BreadcrumbSchema items={breadcrumbItems} />
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="container px-4 py-3">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        {/* Service Areas by County */}
        <section className="py-12 md:py-16">
          <div className="container px-4">
            {Object.entries(groupedByCounty).map(([county, areas]) => (
              <div key={county} className="mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0a1628] mb-6 flex items-center gap-2">
                  <MapPin className="text-[#4caf30]" size={28} />
                  {county}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {areas.map((area) => (
                    <Link key={area.slug} href={`/city/${area.slug}`}>
                      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer border-2 border-gray-200 hover:border-cyan-500">
                        <CardContent className="p-6 flex flex-col h-full">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-[#0a1628] mb-2">
                              {area.name}
                            </h3>
                            <p className="text-gray-600 text-sm mb-4">
                              Professional painting services in {area.name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-[#4caf30] font-semibold mt-4">
                            Learn More
                            <ArrowRight size={18} />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gray-50 py-12 md:py-16">
          <div className="container px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0a1628] mb-4">
                Don't See Your City?
              </h2>
              <p className="text-gray-600 mb-6">
                We may still serve your area. Contact us today to discuss your painting project needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:805-403-8727" onClick={trackPhoneCall}>
                  <Button size="lg" className="bg-[#4caf30] hover:bg-[#3d9426] text-white w-full sm:w-auto">
                    Call: 805-403-8727
                  </Button>
                </a>
                <a href="/#contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    Get Free Quote
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
